<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">Data Downloads</h1>
      <p class="page-subtitle">Access high-quality bioinformatics datasets</p>
    </div>

    <div class="download-info">
      <div class="info-card">
        <h3>Download Guidelines</h3>
        <ul>
          <li>All datasets are provided in standard bioinformatics formats</li>
          <li>Please cite our database when using the data in publications</li>
          <li>For bulk downloads or custom datasets, contact our team</li>
          <li>Data usage is subject to our terms and conditions</li>
        </ul>
      </div>
    </div>

    <div class="download-section">
      <div class="dataset-card" v-for="dataset in datasets" :key="dataset.id">
        <div class="dataset-header">
          <div class="dataset-icon">
            <el-icon><dataset.iconComponent /></el-icon>
          </div>
          <div class="dataset-info">
            <h3 class="dataset-name">{{ dataset.name }}</h3>
            <p class="dataset-meta">
              <span class="meta-item">
                <el-icon><Calendar /></el-icon>
                {{ dataset.date }}
              </span>
              <span class="meta-item">
                <el-icon><Files /></el-icon>
                {{ dataset.size }}
              </span>
              <span class="meta-item">
                <el-icon><Document /></el-icon>
                {{ dataset.format }}
              </span>
            </p>
          </div>
          <div class="dataset-status">
            <el-tag :type="dataset.status === 'available' ? 'success' : 'warning'">
              {{ dataset.status === 'available' ? 'Available' : 'Coming Soon' }}
            </el-tag>
          </div>
        </div>

        <p class="dataset-desc">{{ dataset.description }}</p>

        <div class="dataset-details">
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">Records:</span>
              <span class="detail-value">{{ dataset.records }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Version:</span>
              <span class="detail-value">{{ dataset.version }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">License:</span>
              <span class="detail-value">{{ dataset.license }}</span>
            </div>
          </div>
        </div>

        <div class="dataset-actions">
          <button
            class="download-btn"
            :class="{ disabled: dataset.status !== 'available' }"
            :disabled="dataset.status !== 'available'"
          >
            <el-icon><Download /></el-icon>
            Download
          </button>
          <button class="info-btn">
            <el-icon><InfoFilled /></el-icon>
            Details
          </button>
          <button class="preview-btn">
            <el-icon><View /></el-icon>
            Preview
          </button>
        </div>
      </div>
    </div>

    <div class="bulk-download">
      <div class="bulk-card">
        <h3>Bulk Data Access</h3>
        <p>Need access to complete datasets or custom data exports? Our team can provide:</p>
        <ul>
          <li>Complete database dumps</li>
          <li>Custom filtered datasets</li>
          <li>API access for automated downloads</li>
          <li>Regular data synchronization</li>
        </ul>
        <button class="contact-btn">
          <el-icon><Message /></el-icon>
          Contact Data Team
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Calendar,
  Files,
  Document,
  Download,
  InfoFilled,
  View,
  Message,
  Cpu,
  Operation,
  DataAnalysis
} from '@element-plus/icons-vue'

export default {
  name: 'DataDownload',
  components: {
    Calendar,
    Files,
    Document,
    Download,
    InfoFilled,
    View,
    Message,
    Cpu,
    Operation,
    DataAnalysis
  },
  data() {
    return {
      datasets: [
        {
          id: 1,
          name: 'Allele-Specific Expression Dataset',
          iconComponent: 'DataAnalysis',
          date: '2024-01-15',
          size: '2.3 GB',
          format: 'CSV/JSON',
          records: '3,772,738',
          version: 'v2.1',
          license: 'CC BY 4.0',
          status: 'available',
          description: 'Comprehensive allele-specific expression data with genomic coordinates, read counts, and statistical annotations. Includes data from multiple tissue types and experimental conditions.'
        },
        {
          id: 2,
          name: 'Methylation Analysis Dataset',
          iconComponent: 'Cpu',
          date: '2024-01-10',
          size: '1.8 GB',
          format: 'BED/TXT',
          records: '5,423,891',
          version: 'v1.5',
          license: 'CC BY 4.0',
          status: 'available',
          description: 'Genome-wide methylation data with CpG site annotations, methylation levels, and differential methylation analysis results across various cell types.'
        },
        {
          id: 3,
          name: 'AMR Region Annotations',
          iconComponent: 'Operation',
          date: '2024-01-05',
          size: '850 MB',
          format: 'GTF/GFF',
          records: '2,390,065',
          version: 'v3.0',
          license: 'CC0',
          status: 'available',
          description: 'Allele-specific methylation region annotations with statistical significance scores, genomic coordinates, and functional classifications.'
        },
        {
          id: 4,
          name: 'Integrated Multi-Omics Dataset',
          iconComponent: 'DataAnalysis',
          date: '2024-02-01',
          size: '4.2 GB',
          format: 'HDF5/JSON',
          records: '1,234,567',
          version: 'v1.0',
          license: 'CC BY 4.0',
          status: 'coming_soon',
          description: 'Integrated dataset combining expression, methylation, and variant data with cross-omics correlation analysis and pathway enrichment results.'
        }
      ]
    }
  }
}
</script>

<style scoped>
.page-container {
  min-height: calc(100vh - 150px);
  padding: 60px 20px;
}

.page-header {
  text-align: center;
  margin-bottom: 50px;
}

.page-title {
  font-size: 42px;
  background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 15px;
}

.page-subtitle {
  font-size: 18px;
  color: #666;
}

.download-info {
  max-width: 900px;
  margin: 0 auto 40px;
}

.info-card {
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  border-left: 4px solid #3498db;
}

.info-card h3 {
  margin: 0 0 20px 0;
  color: #2c3e50;
  font-size: 20px;
}

.info-card ul {
  margin: 0;
  padding-left: 20px;
}

.info-card li {
  margin-bottom: 10px;
  color: #555;
  line-height: 1.6;
}

.download-section {
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.dataset-card {
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  transition: all 0.3s;
  border: 1px solid #f0f0f0;
}

.dataset-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.dataset-header {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 20px;
}

.dataset-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  flex-shrink: 0;
}

.dataset-info {
  flex: 1;
}

.dataset-name {
  font-size: 22px;
  margin: 0 0 12px 0;
  color: #2c3e50;
  font-weight: 600;
}

.dataset-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin: 0;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #666;
  font-size: 14px;
}

.meta-item .el-icon {
  font-size: 16px;
}

.dataset-status {
  flex-shrink: 0;
}

.dataset-desc {
  color: #555;
  line-height: 1.7;
  margin-bottom: 25px;
  font-size: 15px;
}

.dataset-details {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 25px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.detail-label {
  font-size: 12px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

.detail-value {
  font-size: 14px;
  color: #2c3e50;
  font-weight: 600;
}

.dataset-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.download-btn,
.info-btn,
.preview-btn {
  padding: 12px 24px;
  border: none;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.download-btn {
  background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%);
  color: white;
}

.download-btn:not(.disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(52, 152, 219, 0.3);
}

.download-btn.disabled {
  background: #e0e0e0;
  color: #999;
  cursor: not-allowed;
}

.info-btn {
  background: white;
  border: 2px solid #3498db;
  color: #3498db;
}

.info-btn:hover {
  background: #3498db;
  color: white;
}

.preview-btn {
  background: white;
  border: 2px solid #2c3e50;
  color: #2c3e50;
}

.preview-btn:hover {
  background: #2c3e50;
  color: white;
}

.bulk-download {
  max-width: 900px;
  margin: 50px auto 0;
}

.bulk-card {
  background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%);
  color: white;
  border-radius: 15px;
  padding: 40px;
  text-align: center;
}

.bulk-card h3 {
  margin: 0 0 20px 0;
  font-size: 24px;
}

.bulk-card p {
  margin: 0 0 20px 0;
  font-size: 16px;
  opacity: 0.9;
}

.bulk-card ul {
  text-align: left;
  display: inline-block;
  margin: 0 0 30px 0;
}

.bulk-card li {
  margin-bottom: 8px;
  opacity: 0.9;
}

.contact-btn {
  background: white;
  color: #2c3e50;
  border: none;
  padding: 15px 30px;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.contact-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(255, 255, 255, 0.3);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-container {
    padding: 40px 15px;
  }

  .dataset-card {
    padding: 20px;
  }

  .dataset-header {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .dataset-meta {
    justify-content: center;
  }

  .dataset-actions {
    justify-content: center;
  }

  .page-title {
    font-size: 32px;
  }

  .bulk-card {
    padding: 30px 20px;
  }
}
</style>